import { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useSession } from '../lib/session';
import { readAccessClaims, refreshAccessClaims } from '../lib/jwt-claims';

/**
 * Route guard for paid-content surfaces. Admin/developer bypass. Everyone
 * else must have has_active_access === true; if not, redirect to
 * /subscribe.
 *
 * Phase 2: reads the JWT claim populated by the Custom Access Token Hook
 * (migration 015) instead of round-tripping to has_active_access RPC.
 * If the claim says no, refreshes the session ONCE and re-reads - covers
 * the case where the user just subscribed but their JWT is stale.
 */

export function RequireAccess({ children }: { children: JSX.Element }) {
  const { profile, loading } = useSession();
  const location = useLocation();
  const [checked, setChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    if (loading || !profile) return;
    let cancelled = false;
    (async () => {
      const claims = await readAccessClaims();
      if (cancelled) return;
      if (claims?.hasActiveAccess || claims?.userRole === 'admin' || claims?.userRole === 'developer') {
        setHasAccess(true);
        setChecked(true);
        return;
      }
      // Claim says no - refresh once in case the JWT is stale (just paid,
      // got promoted to admin, etc.) and re-read.
      const fresh = await refreshAccessClaims();
      if (cancelled) return;
      const ok = Boolean(
        fresh?.hasActiveAccess || fresh?.userRole === 'admin' || fresh?.userRole === 'developer',
      );
      setHasAccess(ok);
      setChecked(true);
    })();
    return () => { cancelled = true; };
  }, [profile?.id, profile?.role, loading]);

  if (loading || !checked) {
    return (
      <div style={{ minHeight: '40dvh', display: 'grid', placeItems: 'center', color: 'var(--text-soft)' }}>
        …
      </div>
    );
  }

  if (!hasAccess) {
    return <Navigate to="/subscribe" replace state={{ from: location }} />;
  }
  return children;
}
